import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from "react-dom";
import {useDispatch, useSelector} from "react-redux";
import {login} from "../../../../actions/user";
import RegistrierenDialog from "../../../RegistrierenDialog";
import ZugangVergessenDialog from "../../../ZugangVergessenDialog";

/**
 * Login im neuen Design als Dialog in der Mitte der Seite. Ersetzt in den neuen Ansichten den LoginDialog
 * (Drawer mit LoginContainer); angemeldet wird wie dort über die Action login. Nach erfolgreichem Login schließt er sich.
 */
export default function LoginDialog({onClose}) {

    const dispatch = useDispatch();
    const isLoggingIn = useSelector(state => state.user.isLoggingIn);

    const [name, setName] = useState("");
    const [kennwort, setKennwort] = useState("");
    const [kennwortSichtbar, setKennwortSichtbar] = useState(false);
    const [fehler, setFehler] = useState(undefined);
    const [showRegistrieren, setShowRegistrieren] = useState(false);
    const [showZugangVergessen, setShowZugangVergessen] = useState(false);
    const nameRef = useRef(null);

    const unterdialogOffen = showRegistrieren || showZugangVergessen;

    useEffect(() => {
        // die Seite dahinter soll nicht mitscrollen
        const overflowVorher = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        if (nameRef.current) {
            nameRef.current.focus();
        }
        return () => {
            document.body.style.overflow = overflowVorher;
        };
    }, []);

    useEffect(() => {
        // solange Registrieren bzw. Zugang vergessen offen ist, schließt Escape nur diese
        if (unterdialogOffen) {
            return;
        }
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [unterdialogOffen, onClose]);

    const kannAnmelden = name.trim().length > 0 && kennwort.length > 0 && !isLoggingIn;

    const anmelden = (event) => {
        event.preventDefault();
        if (!kannAnmelden) {
            return;
        }
        setFehler(undefined);
        dispatch(login(name, kennwort, () => onClose(), (meldung) => setFehler(meldung)));
    }

    const dialog = <div className="dhpt-modal-hintergrund" onMouseDown={(event) => {
        // Klick neben den Dialog schließt ihn
        if (event.target === event.currentTarget) {
            onClose();
        }
    }}>
        <div className="dhpt-modal dhpt-login" role="dialog" aria-modal="true" aria-labelledby="dhpt-login-titel">
            <div className="dhpt-modal-kopf">
                <h2 id="dhpt-login-titel" className="dhpt-modal-titel"><i className="pi pi-sign-in" aria-hidden="true"/>Login</h2>
                <button type="button" className="dhpt-modal-schliessen" aria-label="Schließen" onClick={() => onClose()}>
                    <i className="pi pi-times" aria-hidden="true"/>
                </button>
            </div>
            <p className="dhpt-hinweis dhpt-login-text">
                Um Onlineligen oder Onlineturniere anschauen zu können oder um dich bei einer Liga bzw. einem Turnier anzumelden, musst du angemeldet sein.
            </p>
            <form className="dhpt-login-form" onSubmit={anmelden} noValidate>
                <label className="dhpt-feld">
                    <span className="dhpt-feld-label">Benutzername</span>
                    <input ref={nameRef} type="text" className="dhpt-feld-eingabe" autoComplete="username" autoCapitalize="none" spellCheck={false}
                           value={name} onChange={(event) => setName(event.target.value)}/>
                </label>
                <label className="dhpt-feld">
                    <span className="dhpt-feld-label">Kennwort</span>
                    <span className="dhpt-feld-mit-knopf">
                        <input type={kennwortSichtbar ? "text" : "password"} className="dhpt-feld-eingabe" autoComplete="current-password"
                               value={kennwort} onChange={(event) => setKennwort(event.target.value)}/>
                        <button type="button" className="dhpt-feld-knopf" aria-label={kennwortSichtbar ? "Kennwort verbergen" : "Kennwort anzeigen"}
                                aria-pressed={kennwortSichtbar} onClick={() => setKennwortSichtbar(!kennwortSichtbar)}>
                            <i className={kennwortSichtbar ? "pi pi-eye-slash" : "pi pi-eye"} aria-hidden="true"/>
                        </button>
                    </span>
                </label>
                <p className="dhpt-hinweis dhpt-login-tipp">Mit dem Benutzernamen anmelden, nicht mit der E-Mail-Adresse. Groß- und Kleinschreibung beachten.</p>
                {fehler && <p className="dhpt-login-fehler" role="alert"><i className="pi pi-exclamation-triangle" aria-hidden="true"/>{fehler}</p>}
                <button type="submit" className="dhpt-knopf-primaer" disabled={!kannAnmelden}>
                    {isLoggingIn ? <><i className="pi pi-spin pi-spinner" aria-hidden="true"/>Anmelden …</> : "Anmelden"}
                </button>
            </form>
            <div className="dhpt-login-weitere">
                <span className="dhpt-hinweis">Noch keinen Zugang? Er ist kostenlos.</span>
                <div className="dhpt-login-links">
                    <button type="button" className="dhpt-knopf-sekundaer" onClick={() => setShowRegistrieren(true)}>
                        <i className="pi pi-user-plus" aria-hidden="true"/>Registrieren
                    </button>
                    <button type="button" className="dhpt-knopf-sekundaer" onClick={() => setShowZugangVergessen(true)}>
                        <i className="pi pi-key" aria-hidden="true"/>Zugang vergessen
                    </button>
                </div>
            </div>
        </div>
        {showRegistrieren && <RegistrierenDialog onClose={() => setShowRegistrieren(false)}/>}
        {showZugangVergessen && <ZugangVergessenDialog onClose={() => setShowZugangVergessen(false)}/>}
    </div>;

    // direkt in die Seite hängen (Farbvariablen aus .dhpt-seite), unabhängig vom Ort des Aufrufs
    const ziel = document.querySelector(".dhpt-seite");
    return ziel ? createPortal(dialog, ziel) : dialog;
}
